import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLang, SITE_URL } from "@/i18n";

interface SeoOptions {
  title: string;
  description: string;
  canonical?: string;
  jsonLd?: Record<string, unknown>;
  ogImage?: string;
  ogType?: string;
  /** Ajoute <meta name="robots" content="noindex, nofollow"> (pages internes) */
  noindex?: boolean;
}

const DEFAULT_OG_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/IqkpvueyvNPyxiSFs5Lrgq6k4j62/social-images/social-1775507838632-LOGO.webp";

const upsertMeta = (selector: string, attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  const previous = el?.content;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
  return { el, previous };
};

export const useSeo = ({ title, description, canonical: rawCanonical, jsonLd, ogImage, ogType = "website", noindex = false }: SeoOptions) => {
  // On the English version of a page the canonical must self-reference /en/...
  const lang = useLang();
  const canonical = (() => {
    if (!rawCanonical) return rawCanonical;
    const localized =
      lang === "en" && rawCanonical.startsWith(SITE_URL) && !rawCanonical.startsWith(`${SITE_URL}/en`)
        ? rawCanonical.replace(SITE_URL, `${SITE_URL}/en`)
        : rawCanonical;
    // Évite "/en/" (le sitemap et les hreflang annoncent "/en")
    return localized === `${SITE_URL}/en/` ? `${SITE_URL}/en` : localized;
  })();

  // Optional override loaded from the jsonld_overrides table.
  // When the SEO admin "applies" a corrected JSON-LD on a URL, every visitor
  // (including Googlebot) gets the override instead of the default jsonLd prop.
  const [override, setOverride] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    if (!canonical) {
      setOverride(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("jsonld_overrides")
        .select("jsonld")
        .eq("url", canonical)
        .eq("active", true)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        setOverride(null);
        return;
      }
      setOverride((data.jsonld as Record<string, unknown>) ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [canonical]);

  const effectiveJsonLd = override ?? jsonLd;

  useEffect(() => {
    if (!noindex) return;
    // Réutilise la balise robots statique d'index.html pour éviter deux
    // directives contradictoires sur la même page.
    const existing = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (existing) {
      const previous = existing.content;
      existing.content = "noindex, nofollow";
      return () => {
        existing.content = previous;
      };
    }
    const el = document.createElement("meta");
    el.name = "robots";
    el.content = "noindex, nofollow";
    document.head.appendChild(el);
    return () => el.remove();
  }, [noindex]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    // Meta description
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = meta?.content;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = description;

    // Canonical
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const previousCanonical = link?.href;
    if (canonical) {
      if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
      }
      link.href = canonical;
    }

    // Open Graph + Twitter
    const image = ogImage || DEFAULT_OG_IMAGE;
    const ogTags = [
      upsertMeta('meta[property="og:title"]', "property", "og:title", title),
      upsertMeta('meta[property="og:description"]', "property", "og:description", description),
      upsertMeta('meta[property="og:type"]', "property", "og:type", ogType),
      upsertMeta('meta[property="og:image"]', "property", "og:image", image),
      upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title),
      upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description),
      upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", image),
    ];
    if (canonical) {
      ogTags.push(upsertMeta('meta[property="og:url"]', "property", "og:url", canonical));
    }

    // JSON-LD (override from DB takes precedence over the page's default)
    let script: HTMLScriptElement | null = null;
    if (effectiveJsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-page-jsonld", "true");
      if (override) script.setAttribute("data-source", "override");
      script.textContent = JSON.stringify(effectiveJsonLd);
      document.head.appendChild(script);
    }

    return () => {
      document.title = previousTitle;
      if (meta && previousDescription !== undefined) meta.content = previousDescription;
      if (link && previousCanonical) link.href = previousCanonical;
      ogTags.forEach(({ el, previous }) => {
        if (previous !== undefined) el.content = previous;
      });
      if (script) script.remove();
    };
  }, [title, description, canonical, ogImage, ogType, JSON.stringify(effectiveJsonLd), Boolean(override)]);
};
